/* eslint-disable no-console */
import { useRef, useEffect } from 'react';
import { toJS, isObservable } from 'mobx';
import { isEqual, pickBy } from 'lodash';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';

function copyFlowAttributes(flow) {
    const {
        _id,
        title,
        company_id,
        is_template,
        is_published,
        last_action_number,
        project_id,
        workspace_id,
        flow_url,
        published_at,
        last_changed_at
    } = flow;

    const copy = {
        _id,
        title,
        company_id,
        is_template,
        is_published,
        pageIds: toJS(flow.pageIds),
        last_action_number,
        project_id,
        workspace_id,
        published_by_user: toJS(flow.published_by_user),
        flow_url,
        members: toJS(flow.members),
        theme: toJS(flow._theme),
        status: toJS(flow.status),
        published_at,
        last_changed_at,
        configuration: toJS(flow.configuration),
        last_content_edit: toJS(flow.last_content_edit),
        pages: undefined,
        variables: undefined
    };

    delete copy.configuration.rootStore;

    return copy;
}

function getObservablesFromObject(object) {
    const retData = {};

    Object.keys(object).forEach(key => {
        if (isObservable(object[key])) {
            retData[key] = object[key];
        }
    });

    return retData;
}

function getBlockChildrenRecursive(block, flowStore) {
    const formattedBlock =
        typeof block.getDataForServer === 'function'
            ? block.getDataForServer()
            : getObservablesFromObject(block);

    if (block instanceof ContainerableBlock) {
        const children = block.childBlocks;
        if (children) {
            formattedBlock.children = [];
            children.forEach(childBlock => {
                formattedBlock.children.push(
                    getBlockChildrenRecursive(childBlock, flowStore)
                );
            });
        }
    }

    if (block.connected_variable_id && flowStore.variables) {
        const variable = flowStore.variables[block.connected_variable_id];
        if (variable) {
            formattedBlock.connectedVariable =
                typeof variable.getDataForServer === 'function'
                    ? variable.getDataForServer()
                    : getObservablesFromObject(variable);
        }
    }

    return toJS(formattedBlock);
}

export function getAllFlowsFormatted(rootStore) {
    const { flowStore } = rootStore;
    const formattedFlows = [];

    Object.values(flowStore.flows).forEach(flow => {
        const formattedFlow = copyFlowAttributes(flow);

        formattedFlow.pages = [];
        // @ts-ignore
        flow.pages.forEach(page => {
            const formattedPage =
                typeof page.getDataForServer === 'function'
                    ? page.getDataForServer()
                    : getObservablesFromObject(page);
            // @ts-ignore
            formattedPage.blocks = getBlockChildrenRecursive(
                page.rootBlock,
                flowStore
            );
            formattedFlow.pages.push(formattedPage);
        });

        // @ts-ignore
        if (flow.variables) {
            formattedFlow.variables = [];
        }
        // @ts-ignore
        flow.variables.forEach(variable => {
            const varProperties =
                typeof variable.getDataForServer === 'function'
                    ? variable.getDataForServer()
                    : getObservablesFromObject(variable);
            formattedFlow.variables.push(toJS(varProperties));
        });

        formattedFlows.push(formattedFlow);
    });

    return formattedFlows;
}

export const devEnvAttentionLog = (message: string) => {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }

    console.log(
        '%c' + `🤯${message}`,
        'color: red; font-size: 20px; font-weight: bold;'
    );
};

export const getDifferencesBetweenObjs = (
    obj1: Record<string, any>,
    obj2: Record<string, any>
) => {
    const differences = {
        removed: pickBy(obj1, (value, key) => {
            return !isEqual(value, obj2[key]);
        }),
        added: pickBy(obj2, (value, key) => {
            return !isEqual(obj1[key], value);
        })
    };

    return differences;
};

export const useValueChangeLogger = (values: object) => {
    const previousValues = useRef<object>({});

    if (!Object.keys(previousValues).length) {
        return;
    }

    Object.keys(values).forEach(key => {
        if (previousValues.current[key] !== values[key]) {
            const isPrimitive = ['string', 'number', 'boolean'].includes(
                typeof values[key]
            );
            if (isPrimitive) {
                console.log(
                    `LOG: Value of "${key}" changed from: ${previousValues.current[key]} to: ${values[key]}`
                );
            } else {
                console.log(`LOG: Reference of "${key}" changed`);
            }
        }
    });

    previousValues.current = values;
};

export function printProxyObject(obj: object) {
    console.log('LOG:', JSON.stringify(toJS(obj), null, 2));
}

export const useLogMountUnmount = (
    { componentName }: { componentName: string } = { componentName: 'LOG: ' }
) => {
    useEffect(() => {
        console.log(`${componentName} mounted`);
        return () => {
            console.log(`${componentName} unmounted`);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
